import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsByAgentIdReleasesQueryKey, getOrgAgentsByAgentIdSummaryQueryKey, patchOrgAgentsByAgentIdMutation, postOrgAgentsByAgentIdBuilderPublishMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { trackAction } from '@onefootprint/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetChatResourceListener } from '@/components/agent-chat/components/chat-widget/chat-resource-listener-context';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useBuilderNavigation from '../../hooks/use-builder-navigation';
import { useBuilder } from '../../store/provider';
import { viewForOp } from '../../store/suggestion-sections';
import type { Suggestion, ViewMode } from '../../store/types';
import Canvas from '../canvas';
import { shouldAutoOpenChanges } from '../changes/auto-open';
import CredentialsView from '../credentials-view';
import Header from '../header';
import RightSidebar, { SIDEBAR_TABS } from '../history-sidebar';
import McpView from '../mcp-view';
import Nav from '../nav';
import NavigationControls from '../navigation-controls';
import NormsView from '../norms-view';
import PreviewFooter from '../preview-footer';
import SkillsView from '../skills-view';
import ToolsView from '../tools-view';
import VaultProxyView from '../vault-proxy-view';
type BuilderProps = {
  initialNavOpen?: boolean;
};
const Builder = t0 => {
  const $ = _c(123);
  const {
    initialNavOpen: t1
  } = t0;
  const initialNavOpen = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t2);
  const {
    state,
    dispatch,
    applyResponse,
    flushPendingOps,
    refresh
  } = useBuilder();
  const pendingCount = state.bundle.suggestions.length;
  const {
    viewMode,
    setViewMode
  } = useBuilderNavigation();
  const [sidebarOpen, setSidebarOpen] = useState(pendingCount > 0);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = parseAsStringLiteral(SIDEBAR_TABS).withDefault("changes");
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [sidebarTab, setSidebarTab] = useQueryState("tab", t3);
  const isMobile = useMobileLayout();
  const [navOpen, setNavOpen] = useState(initialNavOpen);
  let t4;
  if ($[2] !== isMobile || $[3] !== setViewMode) {
    t4 = view => {
      setViewMode(view);
      if (isMobile) {
        setNavOpen(false);
      }
    };
    $[2] = isMobile;
    $[3] = setViewMode;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const changeView = t4;
  const prevPendingRef = useRef(pendingCount);
  const setChatResourceListener = useSetChatResourceListener();
  let t5;
  if ($[5] !== pendingCount || $[6] !== refresh || $[7] !== setChatResourceListener || $[8] !== setSidebarTab || $[9] !== state.session?.agentId) {
    t5 = () => {
      setChatResourceListener({
        onResourceEvent: event => {
          if (event.resource.kind === "agent") {
            refresh();
          }
        },
        onReady: () => {
          refresh({
            silent: true
          });
        },
        openSuggestions: agentId => {
          if (agentId !== state.session?.agentId || pendingCount === 0) {
            return false;
          }
          setSidebarOpen(true);
          setSidebarTab("changes");
          return true;
        }
      });
      return () => setChatResourceListener(null);
    };
    $[5] = pendingCount;
    $[6] = refresh;
    $[7] = setChatResourceListener;
    $[8] = setSidebarTab;
    $[9] = state.session?.agentId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = state.session?.agentId;
  let t7;
  if ($[11] !== pendingCount || $[12] !== refresh || $[13] !== setChatResourceListener || $[14] !== setSidebarTab || $[15] !== t6) {
    t7 = [setChatResourceListener, refresh, t6, pendingCount, setSidebarTab];
    $[11] = pendingCount;
    $[12] = refresh;
    $[13] = setChatResourceListener;
    $[14] = setSidebarTab;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useEffect(t5, t7);
  let t8;
  let t9;
  if ($[17] !== pendingCount || $[18] !== setSidebarTab || $[19] !== sidebarOpen) {
    t8 = () => {
      const prev = prevPendingRef.current;
      prevPendingRef.current = pendingCount;
      if (shouldAutoOpenChanges(prev, pendingCount, sidebarOpen)) {
        setSidebarOpen(true);
        setSidebarTab("changes");
      }
    };
    t9 = [pendingCount, sidebarOpen, setSidebarTab];
    $[17] = pendingCount;
    $[18] = setSidebarTab;
    $[19] = sidebarOpen;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  if ($[22] !== dispatch || $[23] !== isMobile || $[24] !== setViewMode) {
    t10 = suggestion => {
      const view_0 = viewForOp(suggestion.operation);
      setViewMode(view_0);
      if (isMobile) {
        setSidebarOpen(false);
      }
      dispatch({
        type: "setFocusedSuggestion",
        id: suggestion.id
      });
      if (view_0 === "plan") {
        requestAnimationFrame(() => dispatch({
          type: "scrollTo",
          id: suggestion.id
        }));
      }
    };
    $[22] = dispatch;
    $[23] = isMobile;
    $[24] = setViewMode;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const navigateToSuggestion = t10;
  const router = useRouter();
  const queryClient = useQueryClient();
  const showErrorToast = useRequestErrorToast();
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = patchOrgAgentsByAgentIdMutation();
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== showErrorToast) {
    t12 = {
      ...t11,
      onError: showErrorToast
    };
    $[27] = showErrorToast;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const patchAgent = useMutation(t12);
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = postOrgAgentsByAgentIdBuilderPublishMutation();
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== showErrorToast) {
    t14 = {
      ...t13,
      onError: showErrorToast
    };
    $[30] = showErrorToast;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const publishVersion = useMutation(t14);
  let t15;
  if ($[32] !== applyResponse || $[33] !== flushPendingOps || $[34] !== publishVersion || $[35] !== queryClient || $[36] !== state.bundle.nodes || $[37] !== state.session) {
    t15 = async t16 => {
      const {
        versionTag,
        publishNotes
      } = t16;
      if (!state.session) {
        return false;
      }
      const {
        agentId: agentId_0,
        agentVersionId
      } = state.session;
      const nodeCount = state.bundle.nodes.length;
      await flushPendingOps();
      try {
        const response = await publishVersion.mutateAsync({
          path: {
            agentId: agentId_0
          },
          body: {
            versionTag,
            publishNotes,
            expectedHeadId: agentVersionId
          }
        });
        applyResponse(response);
        await queryClient.invalidateQueries({
          queryKey: getOrgAgentsByAgentIdReleasesQueryKey({
            path: {
              agentId: agentId_0
            }
          })
        });
        await queryClient.invalidateQueries({
          queryKey: getOrgAgentsByAgentIdSummaryQueryKey({
            path: {
              agentId: agentId_0
            }
          })
        });
        trackAction("agent_published", {
          agent_id: agentId_0,
          version_tag: versionTag,
          node_count: nodeCount
        });
        return true;
      } catch {
        return false;
      }
    };
    $[32] = applyResponse;
    $[33] = flushPendingOps;
    $[34] = publishVersion;
    $[35] = queryClient;
    $[36] = state.bundle.nodes;
    $[37] = state.session;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const handlePublishRelease = t15;
  let t16;
  if ($[39] !== handlePublishRelease || $[40] !== router) {
    t16 = async () => {
      const published = await handlePublishRelease({
        versionTag: "0.0.1",
        publishNotes: "InitialVersion"
      });
      if (published) {
        router.push("/agents");
      }
    };
    $[39] = handlePublishRelease;
    $[40] = router;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const handleCreate = t16;
  const t17 = !isMobile || navOpen;
  let t18;
  if ($[42] !== isMobile) {
    t18 = isMobile ? () => setNavOpen(false) : undefined;
    $[42] = isMobile;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t18) {
    t19 = <NavigationControls onClose={t18} />;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== changeView || $[47] !== t17 || $[48] !== t19 || $[49] !== viewMode) {
    t20 = <Nav open={t17} viewMode={viewMode} onViewModeChange={changeView} header={t19} />;
    $[46] = changeView;
    $[47] = t17;
    $[48] = t19;
    $[49] = viewMode;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== isMobile || $[52] !== navOpen || $[53] !== t) {
    t21 = isMobile && navOpen && <button type="button" aria-label={t(_temp)} className="absolute inset-0 z-20 bg-black/30" onClick={() => setNavOpen(false)} />;
    $[51] = isMobile;
    $[52] = navOpen;
    $[53] = t;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== isMobile || $[56] !== sidebarOpen) {
    t22 = isMobile && sidebarOpen && <motion.button type="button" aria-hidden={true} tabIndex={-1} className="absolute inset-0 z-10 bg-black/30" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={() => setSidebarOpen(false)} />;
    $[55] = isMobile;
    $[56] = sidebarOpen;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t22) {
    t23 = <AnimatePresence>{t22}</AnimatePresence>;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  const t24 = patchAgent.isPending || publishVersion.isPending;
  let t25;
  if ($[60] !== setSidebarTab || $[61] !== sidebarOpen) {
    t25 = () => {
      if (!sidebarOpen) {
        setSidebarTab("history");
      }
      setSidebarOpen(_temp2);
    };
    $[60] = setSidebarTab;
    $[61] = sidebarOpen;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== isMobile) {
    t26 = isMobile ? () => setNavOpen(true) : undefined;
    $[63] = isMobile;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== handleCreate || $[66] !== handlePublishRelease || $[67] !== setViewMode || $[68] !== sidebarOpen || $[69] !== t24 || $[70] !== t25 || $[71] !== t26 || $[72] !== viewMode) {
    t27 = <Header viewMode={viewMode} onCreate={handleCreate} onPublishRelease={handlePublishRelease} isSubmitting={t24} onViewModeChange={setViewMode} sidebarOpen={sidebarOpen} onToggleSidebar={t25} onOpenNav={t26} />;
    $[65] = handleCreate;
    $[66] = handlePublishRelease;
    $[67] = setViewMode;
    $[68] = sidebarOpen;
    $[69] = t24;
    $[70] = t25;
    $[71] = t26;
    $[72] = viewMode;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== viewMode) {
    t28 = viewMode === "plan" && <Canvas />;
    $[74] = viewMode;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== viewMode) {
    t29 = viewMode === "tools" && <ToolsView />;
    $[76] = viewMode;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== viewMode) {
    t30 = viewMode === "mcp" && <McpView />;
    $[78] = viewMode;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== viewMode) {
    t31 = viewMode === "vaultProxies" && <VaultProxyView />;
    $[80] = viewMode;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== viewMode) {
    t32 = viewMode === "norms" && <NormsView />;
    $[82] = viewMode;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== viewMode) {
    t33 = viewMode === "skills" && <SkillsView />;
    $[84] = viewMode;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== viewMode) {
    t34 = viewMode === "credentials" && <CredentialsView />;
    $[86] = viewMode;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== t28 || $[89] !== t29 || $[90] !== t30 || $[91] !== t31 || $[92] !== t32 || $[93] !== t33 || $[94] !== t34) {
    t35 = <main className="min-w-0 flex-1 overflow-y-auto overscroll-none scroll-pt-8 bg-primary">{t28}{t29}{t30}{t31}{t32}{t33}{t34}</main>;
    $[88] = t28;
    $[89] = t29;
    $[90] = t30;
    $[91] = t31;
    $[92] = t32;
    $[93] = t33;
    $[94] = t34;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] !== state.current?.isHistoricalPreview) {
    t36 = state.current?.isHistoricalPreview && <PreviewFooter key="preview" />;
    $[96] = state.current?.isHistoricalPreview;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  let t37;
  if ($[98] !== t36) {
    t37 = <AnimatePresence mode="wait">{t36}</AnimatePresence>;
    $[98] = t36;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  let t38;
  if ($[100] !== t35 || $[101] !== t37) {
    t38 = <div className="relative flex min-w-0 flex-1">{t35}{t37}</div>;
    $[100] = t35;
    $[101] = t37;
    $[102] = t38;
  } else {
    t38 = $[102];
  }
  let t39;
  if ($[103] !== isMobile || $[104] !== navigateToSuggestion || $[105] !== pendingCount || $[106] !== setSidebarTab || $[107] !== sidebarOpen || $[108] !== sidebarTab) {
    t39 = sidebarOpen && <RightSidebar tab={sidebarTab} onTabChange={setSidebarTab} onNavigateToSuggestion={navigateToSuggestion} pendingCount={pendingCount} onSelectVersion={isMobile ? () => setSidebarOpen(false) : undefined} />;
    $[103] = isMobile;
    $[104] = navigateToSuggestion;
    $[105] = pendingCount;
    $[106] = setSidebarTab;
    $[107] = sidebarOpen;
    $[108] = sidebarTab;
    $[109] = t39;
  } else {
    t39 = $[109];
  }
  let t40;
  if ($[110] !== t39) {
    t40 = <AnimatePresence>{t39}</AnimatePresence>;
    $[110] = t39;
    $[111] = t40;
  } else {
    t40 = $[111];
  }
  let t41;
  if ($[112] !== t38 || $[113] !== t40) {
    t41 = <div className="relative flex-1 flex overflow-hidden">{t38}{t40}</div>;
    $[112] = t38;
    $[113] = t40;
    $[114] = t41;
  } else {
    t41 = $[114];
  }
  let t42;
  if ($[115] !== t27 || $[116] !== t41) {
    t42 = <div className="flex min-w-0 flex-1 flex-col overflow-hidden">{t27}{t41}</div>;
    $[115] = t27;
    $[116] = t41;
    $[117] = t42;
  } else {
    t42 = $[117];
  }
  let t43;
  if ($[118] !== t20 || $[119] !== t21 || $[120] !== t23 || $[121] !== t42) {
    t43 = <div className="absolute inset-0 flex min-h-0 overflow-hidden">{t20}{t21}{t23}{t42}</div>;
    $[118] = t20;
    $[119] = t21;
    $[120] = t23;
    $[121] = t42;
    $[122] = t43;
  } else {
    t43 = $[122];
  }
  return t43;
};
export default Builder;
function _temp($) {
  return $.canvas.header.toggleNav.ariaLabel;
}
function _temp2(open) {
  return !open;
}